import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Zap, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const SubscriptionCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, failed
  const [message, setMessage] = useState("Verifying your payment...");

  useEffect(() => {
    const verifyPayment = async () => {
      const gateway = searchParams.get("gateway");
      const reference = searchParams.get("reference") || searchParams.get("tx_ref");
      const transactionId = searchParams.get("transaction_id");
      const flwStatus = searchParams.get("status");

      // For Flutterwave, check status from URL first
      if (gateway === "flutterwave" && flwStatus === "cancelled") {
        setStatus("failed");
        setMessage("Payment was cancelled");
        return;
      }

      if (!gateway || (!reference && !transactionId)) {
        setStatus("failed");
        setMessage("Invalid payment callback");
        return;
      }

      try {
        let verifyUrl;
        let verifyRef = reference;

        if (gateway === "paystack") {
          verifyUrl = `${API}/paystack/verify/${reference}`;
        } else if (gateway === "flutterwave") {
          // For Flutterwave, we need to extract the tx_ref
          verifyRef = searchParams.get("tx_ref") || reference;
          verifyUrl = `${API}/flutterwave/verify/${verifyRef}`;
        } else {
          setStatus("failed");
          setMessage("Unknown payment gateway");
          return;
        }

        const response = await fetch(verifyUrl, {
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.status === "success") {
            setStatus("success");
            setMessage("Payment successful! Your subscription is now active.");
            toast.success("Subscription activated!");
            
            // Refresh user data
            const userResponse = await fetch(`${API}/auth/me`, {
              credentials: "include",
            });
            if (userResponse.ok) {
              const userData = await userResponse.json();
              setUser(userData);
            }
          } else if (data.status === "pending") {
            setStatus("verifying");
            setMessage("Payment is still processing. Please wait...");
            
            // Retry verification after a short delay
            setTimeout(() => {
              verifyPayment();
            }, 3000);
          } else {
            setStatus("failed");
            setMessage(data.message || "Payment verification failed");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          setStatus("failed");
          setMessage(errorData.detail || "Failed to verify payment");
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("failed");
        setMessage("An error occurred while verifying your payment");
      }
    };

    verifyPayment();
  }, [searchParams, setUser]);

  const handleContinue = () => {
    if (user?.role === "recruiter") {
      navigate("/dashboard/recruiter");
    } else {
      navigate("/dashboard/jobseeker");
    }
  };

  const handleRetry = () => {
    navigate("/subscription");
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <motion.div
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-crimson flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold">REKRUTOR</span>
        </div>

        {/* Status Icon */}
        <div className="mb-6">
          {status === "verifying" && (
            <div className="w-20 h-20 mx-auto bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
            </div>
          )}
          {status === "success" && (
            <div className="w-20 h-20 mx-auto bg-green-500/10 border border-green-500/30 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          )}
          {status === "failed" && (
            <div className="w-20 h-20 mx-auto bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl font-bold mb-4">
          {status === "verifying" && "VERIFYING PAYMENT"}
          {status === "success" && "PAYMENT SUCCESSFUL"}
          {status === "failed" && "PAYMENT FAILED"}
        </h1>

        {/* Message */}
        <p className="text-gray-400 mb-8">{message}</p>

        {/* Actions */}
        {status === "success" && (
          <Button
            onClick={handleContinue}
            className="w-full bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider py-6"
            data-testid="continue-to-dashboard"
          >
            Continue to Dashboard
          </Button>
        )}

        {status === "failed" && (
          <div className="space-y-3">
            <Button
              onClick={handleRetry}
              className="w-full bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider py-6"
              data-testid="retry-payment"
            >
              Try Again
            </Button>
            <Button
              onClick={handleContinue}
              variant="outline"
              className="w-full border-[#333] hover:border-crimson text-white uppercase tracking-wider"
              data-testid="go-to-dashboard"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {status === "verifying" && (
          <p className="text-sm text-gray-500">
            Please don't close this page...
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionCallback;
